import { useState, useContext, useMemo, useEffect } from "react";
import { useHistory, useLocation, useParams, useRouteMatch } from "react-router-dom";
import { ExternalLinkIcon, XIcon } from "@heroicons/react/outline";
import { WorkflowContext } from "../contexts";
import { MIMETYPE_ICON_MAP } from "../utils";
import { ApprovalRequest, WorkflowAutomationItem } from "../interfaces";
import { getData, postData } from "../backend";
import { useTranslation } from "react-i18next";
import TransitionWorkflowPage from "./transitions/TransitionWorkflowPage";
import TransitionWorkflowPageContent from "./transitions/TransitionWorkflowPageContent";
import TransitionWorkflowPageSideBar from "./transitions/TransitionWorkflowPageSideBar";
import useUser from "../hooks/useUser";
import useSWR from "swr";
import moment from "moment";
import useReviewItems from "../hooks/useReviewItems";

export const APPROVAL_STATUS_MAP: Record<string, Array<string>> = {
  "PENDING": ["Pending", "border-gray-200 bg-gray-100 text-gray-600"],
  "REQUESTED": ["Requested", "border-blue bg-blue/10 text-blue"],
  "APPROVED": ["Approved", "border-green bg-green/10 text-green"],
  "REJECTED": ["Changes Requested", "border-red bg-red/10 text-red"],
  "CANCELED": ["Canceled", "border-red bg-red/10 text-red"],
}

export const CHANGE_REQUEST_STATUS_MAP: Record<string, Array<string>> = {
  "PENDING": ["Pending", "border-gray-200 bg-gray-100 text-gray-600"],
  "REQUESTED": ["Requested", "border-blue bg-blue/10 text-blue"],
  "APPROVED": ["Changes", "border-green bg-green/10 text-green"],
  "REJECTED": ["Rejcted", "border-red bg-red/10 text-red"],
  "CANCELED": ["Canceled", "border-red bg-red/10 text-red"],
}

function WorkflowOutputsDetail() {
  const history = useHistory();
  const { url } = useRouteMatch();
  const { t } = useTranslation();

  const { user } = useUser();

  const location = useLocation();
  const itemId = new URLSearchParams(location.search).get("item");

  const { data: currentItem, mutate } = useSWR<WorkflowAutomationItem>(itemId ? `/workflows/automation-items/${itemId}` : null, getData);

  const { id: workflowId } = useParams<{ id?: string }>();
  const { data: reviewItems, mutate: refreshReviewItems } = useReviewItems(workflowId);

  const { workflow, showAutomationPanel } = useContext(WorkflowContext);
  const [currentOutputIndex, setCurrentOutputIndex] = useState(0);

  const [currentState, setCurrentState] = useState<"INITIAL" | "APPROVING" | "APPROVED" | "DELETING">("INITIAL");

  const [showRequestChanges, setShowRequestChanges] = useState(false);
  const [requestChangesMessage, setRequestChangesMessage] = useState<string>("The document(s) requires updates before approval can continue. Please review the suggested edits and resolve the comments.");

  const latestApprovalRequest = currentItem?.approvalRequests.find((request) => request.status === "REQUESTED");
  const latestApprovalRequestForUser = currentItem?.approvalRequests.find((request) => request.status === "REQUESTED" && request.approver.id === user?.id);

  const canApprove = currentItem?.stage === "REVIEW_PENDING" && ((latestApprovalRequest?.id === latestApprovalRequestForUser?.id) || (!workflow.review?.approvalsInSequence && !!latestApprovalRequestForUser) || currentItem?.approvalRequests.length === 0);

  useEffect(() => {
    setCurrentState("INITIAL");
    setShowRequestChanges(false);
    refreshReviewItems();
  }, [itemId, refreshReviewItems]);

  function isReviewable(item: WorkflowAutomationItem): boolean {
    return item.stage === "REVIEW_PENDING" && ((latestApprovalRequest?.id === latestApprovalRequestForUser?.id) || (!workflow.review?.approvalsInSequence && !!latestApprovalRequestForUser) || item.approvalRequests.length === 0);
  }

  const approversList: Array<ApprovalRequest> = useMemo(() => {
    if (!currentItem?.approvalRequests)
      return [];

    let result: Array<ApprovalRequest> = [];
    for (const approvalRequest of currentItem.approvalRequests) {
      const index = result.findIndex(a => approvalRequest.approver.id === a.approver.id);
      if (index >= 0)
        result[index] = approvalRequest.status !== "PENDING" ? approvalRequest : result[index];
      else
        result.push(approvalRequest);
    }

    return result;
  }, [currentItem?.approvalRequests]);

  function setCurrentItemOutput(item: WorkflowAutomationItem, outputIndex: number) {
    history.push(url + "/review?item=" + item.id);
    setCurrentOutputIndex(outputIndex);
  }

  async function handleApprove() {
    setCurrentState("APPROVING");
    await postData(`/workflows/automation-items/${itemId}/approve/`)
      .then(mutate)
      .catch(console.error);

    setTimeout(moveToNextItem, 1000);
  }

  async function handleRejectItem() {
    const requestChangesUser = currentItem?.initiatingUser?.id;

    setCurrentState("DELETING");
    await postData(`/workflows/automation-items/${itemId}/reject/`, { requestChangesMessage, requestChangesUser })
      .then(mutate)
      .catch(console.error);

    setTimeout(moveToNextItem, 1000);
  }

  function moveToNextItem() {
    mutate();
    setCurrentState("INITIAL");
    setCurrentOutputIndex(0);

    const otherRevieableItems = reviewItems?.filter(item => isReviewable(item) && item.id !== currentItem?.id) ?? [];
    const nextItem = otherRevieableItems[0];

    if (nextItem)
      history.push(url + "/review?" + new URLSearchParams({ item: nextItem.id }).toString());
    else
      history.push(url + "/outputs"); // return to outputs page
  }

  if (!location.pathname.endsWith("/review"))
    return null;

  if (!currentItem || !user || !workflow)
    return (
      <div className="w-full h-full flex flex-col items-center justify-center">
        <div className='w-24 h-24'>
          <img src="https://static.portant.co/portant-loading-white.svg" alt="Loading Logo" />
        </div>
      </div>
    );

  const currentOutput = currentItem.outputs[currentOutputIndex];

  return (
    <TransitionWorkflowPage show={location.pathname.endsWith("/review")}>
      <TransitionWorkflowPageSideBar>

        {!showRequestChanges &&
          <div className={`flex flex-col gap-1 h-full ${showAutomationPanel && "blur-sm"}`}>
            <div className="flex items-center justify-between font-semibold font-gilroy text-gray-600">
              {t("Documents within this item")} ({currentItem.outputs.length})
            </div>

            {currentItem.outputs.map((output, index) =>
              <div key={output.id} className="flex items-center gap-1 w-full ">
                <div className="flex items-center gap-1 w-full">
                  <img className="h-4 w-4 flex-shrink-0" src={MIMETYPE_ICON_MAP[output.onedriveFile?.fileMimeType ?? output.file?.mimeType ?? output.filePdf?.mimeType ?? ""]} alt="File Icon" />
                  <div className={`font-gilroy truncate w-full ${output.id === currentOutput.id ? "text-blue font-semibold" : "text-gray-600 hover:font-semibold"} cursor-pointer`} onClick={() => setCurrentItemOutput(currentItem, index)}>
                    {output.onedriveFile?.fileName ?? output.file?.name ?? output.filePdf?.name}
                  </div>
                </div>
              </div>
            )}

            {approversList.length > 0 &&
              <>
                <div className="mt-4 text-sm font-semibold text-gray-600">Approvers</div>
                <div className="flex flex-col gap-2">
                  {approversList.map((ar) => (
                    <div key={ar.id} className="flex items-center gap-2 font-semibold text-gray-600 text-sm">
                      <img className="h-5 w-5 rounded-full border-2 drop-shadow overflow-hidden" src={ar.approver.avatar} alt={"User Avatar"} />
                      <div>{ar.approver.fullName}</div>
                      <div className={`ml-2 w-fit px-4 font-semibold rounded-full cursor-pointer border ${(ar.isChangeRequest ? CHANGE_REQUEST_STATUS_MAP : APPROVAL_STATUS_MAP)[ar.status]?.[1]}`}>
                        {(ar.isChangeRequest ? CHANGE_REQUEST_STATUS_MAP : APPROVAL_STATUS_MAP)[ar.status]?.[0]}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            }

            {currentItem.approvalRequests.length > 0 &&
              <div className="flex flex-col my-auto gap-1">
                <div className="text-sm font-semibold text-gray-600 ">Activity</div>
                <div className="flex flex-col-reverse gap-2">
                  {currentItem.approvalRequests.filter(ar => ar.status !== "PENDING").map((ar) => (
                    <div key={ar.id} className="flex flex-col">
                      <div className="text-xs text-gray-400">{moment(ar.respondedAt ?? ar.requestedAt).format("DD MMMM, h:mma")}</div>
                      <div className="flex items-center gap-2 font-semibold text-gray-600 text-sm">
                        <img className="h-5 w-5 rounded-full border-2 drop-shadow overflow-hidden" src={ar.approver.avatar} alt={"User Avatar"} />
                        <div>{ar.approver.fullName}</div>
                        <div className={`ml-2 w-fit px-4 font-semibold rounded-full cursor-pointer border ${(ar.isChangeRequest ? CHANGE_REQUEST_STATUS_MAP : APPROVAL_STATUS_MAP)[ar.status]?.[1]}`}>
                          {(ar.isChangeRequest ? CHANGE_REQUEST_STATUS_MAP : APPROVAL_STATUS_MAP)[ar.status]?.[0]}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            }
            {canApprove &&
              <>
                <div className="h-px w-full bg-gray-300 flex-shrink-0 mt-auto" />
                <div className="flex flex-col items-center gap-2 mt-4">
                  {currentState === "INITIAL" &&
                    <button className="btn btn-green text-sm h-10 w-full" onClick={handleApprove}>
                      {latestApprovalRequest?.isChangeRequest ? t("Submit changes") : t("Approve")}
                    </button>
                  }
                  {currentState === "APPROVING" &&
                    <button className="btn btn-blue text-sm h-10 w-full" disabled>
                      {t("Processing...")}
                    </button>
                  }
                  {currentState === "APPROVED" &&
                    <button className="btn btn-green text-sm h-10 w-full" disabled>
                      {t("Approved")}
                    </button>
                  }
                  {/* Request Changes button */}
                  {(currentState === "INITIAL" && !latestApprovalRequest?.isChangeRequest && latestApprovalRequest?.approver.id !== currentItem.initiatingUser?.id && currentItem.approvalRequests.length > 0) &&
                    <button className="btn btn-gray text-sm h-10 w-full" onClick={() => setShowRequestChanges(true)}>
                      {t("Request changes")}
                    </button>
                  }
                </div>
              </>
            }
          </div>
        }

        {!!showRequestChanges &&
          <div className={`flex flex-col gap-1 h-full ${showAutomationPanel && "blur-sm"}`}>
            <div className="flex items-center justify-between font-semibold font-gilroy text-gray-600">
              {t("Request Changes")}
            </div>

            <div className="text-xs">
              {t("Request any changes to be made. Add comments directly in the document for detailed feedback.")}
            </div>

            <div className="h-px w-full bg-gray-300 flex-shrink-0 my-2" />

            <div className="text-xs font-semibold text-gray-600 mt-4">
              Change request to be sent to:
            </div>
            {currentItem.initiatingUser &&
              <div className="flex items-center px-2 gap-2">
                <img className="h-8 w-8 rounded-full border-2 drop-shadow overflow-hidden" src={currentItem.initiatingUser.avatar} alt={"User Avatar"} />
                <div className="flex flex-col items-start">
                  <div className="text-sm font-semibold text-gray-600">
                    {currentItem.initiatingUser.fullName}
                  </div>
                  <div className="text-xs text-gray-400">
                    {currentItem.initiatingUser.email}
                  </div>
                </div>
              </div>
            }

            <div className="text-xs font-semibold text-gray-600 mt-4">
              Message
            </div>
            <textarea className="w-full min-h-32 p-2 border border-gray-300 rounded resize-y text-sm" value={requestChangesMessage} placeholder={t("The document(s) requires updates before approval can continue. Please review the suggested edits and resolve the comments.")} onChange={(e) => setRequestChangesMessage(e.currentTarget.value)} />

            {(latestApprovalRequest?.approver.id === user.id) &&
              <>
                <div className="h-px w-full bg-gray-300 flex-shrink-0 mt-auto" />
                <div className="flex flex-col items-center gap-2 mt-4">
                  {currentState === "INITIAL" &&
                    <button className="btn btn-blue text-sm h-10 w-full" onClick={handleRejectItem}>
                      {t("Submit")}
                    </button>
                  }
                  {currentState === "APPROVING" &&
                    <button className="btn btn-blue text-sm h-10 w-full" disabled>
                      {t("Processing...")}
                    </button>
                  }
                  {currentState === "DELETING" &&
                    <button className="btn btn-red text-sm h-10 w-full" disabled>
                      {t("Submitting...")}
                    </button>
                  }
                  {currentState === "APPROVED" &&
                    <button className="btn btn-green text-sm h-10 w-full" disabled>
                      {t("Submitted")}
                    </button>
                  }
                  {/* Request Changes button */}
                  {currentState === "INITIAL" &&
                    <button className="btn btn-gray text-sm h-10 w-full" onClick={() => setShowRequestChanges(false)}>
                      {t("Back")}
                    </button>
                  }
                </div>
              </>
            }
          </div>
        }
      </TransitionWorkflowPageSideBar>

      {currentOutput.onedriveFile &&
        <TransitionWorkflowPageContent>
          <div className="flex w-full items-center mb-2">
            <div className="flex justify-center items-center my-auto mr-2 rounded w-10 h-10">
              <img className="h-10 w-10 rounded-md" src={MIMETYPE_ICON_MAP[currentOutput.onedriveFile.fileMimeType]} alt="Microsoft Excel File Icon" />
            </div>
            <div className="flex items-center gap-4">
              <div className="flex flex-col h-full justify-center">
                <div className="flex text-sm font-semibold font-gilroy cursor-pointer hover:text-blue hover:underline" onClick={() => window.open(currentOutput.onedriveFile?.fileUrl, "_blank")}>
                  <div className="truncate max-w-[400px]">
                    {currentOutput.onedriveFile.fileName}
                  </div>
                  {currentOutput.onedriveFile.fileUrl &&
                    <ExternalLinkIcon className="h-4 w-4 ml-1 mb-1 group-hover:text-blue" />
                  }
                </div>
              </div>
            </div>
            <XIcon className="w-6 h-6 ml-auto text-gray-400 hover:text-black cursor-pointer" onClick={() => history.push(url)} />
          </div>
          <iframe title={t("Microsoft File Viewer")} height="1000" className="w-full h-full border border-gray-300 rounded overflow-hidden"
            src={currentOutput.onedriveFile.fileEmbedViewUrl} />
        </TransitionWorkflowPageContent>
      }

      {!currentOutput.onedriveFile &&
        <TransitionWorkflowPageContent className="relative">
          <div className="flex w-full items-center mb-2">
            <div className="flex justify-center items-center my-auto mr-2 rounded w-10 h-10">
              <img className="h-10 w-10 rounded-md" src={MIMETYPE_ICON_MAP[currentOutput.file?.mimeType ?? currentOutput.filePdf?.mimeType ?? "application/vnd.google-apps.document"]} alt="Google File Icon" />
            </div>
            <div className="flex flex-col h-full justify-center">
              <div className="flex text-sm font-semibold font-gilroy cursor-pointer hover:text-blue hover:underline" onClick={() => window.open(currentOutput.file?.url ?? currentOutput.filePdf?.url, "_blank")}>
                <div className="truncate max-w-[400px]">
                  {currentOutput.file?.name ?? currentOutput.filePdf?.name}
                </div>
                {(currentOutput.file?.url ?? currentOutput.filePdf?.url) &&
                  <ExternalLinkIcon className="h-4 w-4 ml-1 mb-1 group-hover:text-blue" />
                }
              </div>
            </div>
            <XIcon className="w-6 h-6 ml-auto text-gray-400 hover:text-black cursor-pointer" onClick={() => history.push(url + "/outputs")} />
          </div>

          {(currentOutput.file && currentOutput.file.mimeType !== "application/pdf") &&
            <iframe title={t("Google Editor")} className="w-full h-full border border-gray-300 rounded overflow-hidden" src={`${currentOutput.file.url}?widget&rm=embedded`} />
          }
          {(!currentOutput.file && !!currentOutput.filePdf) &&
            <iframe title={t("Google Drive PDF")} src={`https://drive.google.com/file/d/${currentOutput.filePdf.id}/preview`} className="w-full h-full border border-gray-300 rounded overflow-hidden" />
          }
        </TransitionWorkflowPageContent>
      }

    </TransitionWorkflowPage>
  );

}

export default WorkflowOutputsDetail;
